import React, { useMemo } from "react";
import { FilterPill } from "./FilterPill";
import { useMultiSidebar } from "@/components/ui/MultiSidebar";
import { useTranslation } from "react-i18next";
import { FilteredStudentsList } from "@/components/FilteredStudentsList";
import { FilterContextMeta } from "@/lib/FilterContextMeta";
import { BackToContextLink } from "./BackToContextLink";
import { useFilterSetup } from "@/hooks/useFilterSetup";
import { SmartFilters } from "@/components/SmartFilters";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { RiseAltLogo } from "@/assets/svg/RiseAltLogo";
import { riseFiltersActive } from "./util";
import { filterGroups } from "@/components/FilterSidebar/filterGroups";
import { useSmartFiltersActive } from "@/components/SmartFilters/useSmartFiltersActive";

export const StudentsList = () => {
  const { t } = useTranslation();
  const { toggleSidebar } = useMultiSidebar("filter");
  const { hasOrgFeature } = useOrgPermissions();

  const {
    filterState,
    ordering: studentOrdering,
    context: filterContext,
    setOrdering: setStudentOrdering,
  } = useFilterSetup({});

  const activeFilterCount = filterState?.activeFilterCount() || 0;

  const hasRise = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);

  const contextMeta = FilterContextMeta[filterContext?.data || "organization"];
  const descriptionKeyOverride = contextMeta?.descriptionTransKey;

  const hasRiseFilters = useMemo(
    () => riseFiltersActive(filterState, filterGroups),
    [filterState],
  );
  const sfActive = useSmartFiltersActive(filterState);
  const showRiseSparkle = hasRise && (hasRiseFilters || sfActive);

  return (
    <div>
      <div className="w-full border-b p-6 pb-4">
        <div className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <h1>{t("studentsList.title")}</h1>{" "}
          {hasRise && <RiseAltLogo height={18} width={38} />}
        </div>
        {contextMeta && (
          <BackToContextLink
            title={contextMeta.title}
            path={contextMeta.path}
            className="pt-2"
          />
        )}
      </div>
      <div className="flex flex-col gap-8 p-6">
        <div className="flex justify-end">
          {hasRise && (
            <>
              <div className="min-w-0 shrink grow">
                <SmartFilters />
              </div>
              <div className="mx-3 h-8 w-[1px] bg-border"></div>
            </>
          )}
          <FilterPill
            data-testid="toggle-students-list-filter"
            filterCount={activeFilterCount}
            onClick={toggleSidebar}
          />
        </div>
        <FilteredStudentsList
          filterState={filterState}
          context={filterContext}
          ordering={studentOrdering}
          setOrdering={setStudentOrdering}
          title={t("studentsList.listTitle")}
          riseIcon={showRiseSparkle}
          getDescription={
            descriptionKeyOverride
              ? (count) => t(descriptionKeyOverride, { totalCount: count })
              : undefined
          }
        />
      </div>
    </div>
  );
};
